import { api, withFreshAccess } from './client'

export interface DiscoveredActor {
  did: string
  handle: string
  displayName?: string
  description?: string
  avatar?: string
}

interface SearchArgs {
  accountId: string
  /** A hashtag or keyword. We search POSTS carrying this tag (not
   *  actor handles/bios) and harvest their authors. */
  tag: string
  limit?: number
  cursor?: string
}

interface PostAuthorHit {
  author: DiscoveredActor
  /** Post body — used for deny-tag filtering on actual content. */
  text: string
}

interface PostSearchPage {
  hits: PostAuthorHit[]
  cursor?: string
}

/** Normalise a user-entered tag to the `#hashtag` query form. We search
 *  with `q=#tag` ALONE — exactly what the bsky.app search box does — and
 *  deliberately do NOT add the dedicated `&tag=` facet param: that ANDs
 *  an extra constraint that drops posts which wrote the tag as plain
 *  text without a rich-text facet, so it can return far fewer (or zero)
 *  results than the user sees in the app. */
function tagQuery(raw: string): { q: string; bare: string } {
  const bare = raw.trim().replace(/^#+/, '')
  return { q: `#${bare}`, bare: bare.toLowerCase() }
}

/** Single page of app.bsky.feed.searchPosts for a hashtag, mapped down
 *  to (author, text) hits plus the cursor so the caller can walk
 *  deeper. THIS is the fix for "campaigns find nobody": searchActors
 *  only matched accounts with the tag in their handle/bio, whereas a
 *  hashtag campaign wants the people actually POSTING the tag. */
export async function searchPostAuthorsByTag({
  accountId,
  tag,
  limit = 100,
  cursor,
}: SearchArgs): Promise<PostSearchPage> {
  const { q, bare } = tagQuery(tag)
  if (!bare) return { hits: [], cursor: undefined }
  const r = await withFreshAccess(accountId, () =>
    api.xrpc<{
      posts?: Array<{
        author?: {
          did: string
          handle: string
          displayName?: string
          avatar?: string
        }
        record?: { text?: string }
      }>
      cursor?: string
    }>({
      accountId,
      method: 'GET',
      path:
        'app.bsky.feed.searchPosts?q=' +
        encodeURIComponent(q) +
        `&sort=latest&limit=${limit}` +
        (cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''),
    }),
  )
  const hits: PostAuthorHit[] = []
  for (const p of r.posts ?? []) {
    if (!p.author?.did) continue
    hits.push({
      author: {
        did: p.author.did,
        handle: p.author.handle,
        displayName: p.author.displayName,
        avatar: p.author.avatar,
      },
      text: p.record?.text ?? '',
    })
  }
  return { hits, cursor: r.cursor }
}

/** Hard cap on pagination depth per tag. searchPosts returns up to 100
 *  posts/page but a hot hashtag repeats the same prolific authors, so
 *  once dedupe against existing follows kicks in we need to walk
 *  several pages to surface fresh DIDs — 6 × 100 = 600 posts/tag/tick. */
const MAX_PAGES_PER_TAG = 6

interface PickArgs {
  accountId: string
  tags: string[]
  denyTags: string[]
  want: number
  /** Set of DIDs the campaign must not consider — populated by the
   *  caller with current follows + already-queued follow targets. */
  excludeDids: Set<string>
}

/**
 * Pull candidates for one campaign tick. For each tag, searches recent
 * POSTS carrying that hashtag, harvests their authors, dedupes by DID,
 * drops anyone already followed / queued, and filters out authors whose
 * matched post or handle hits a deny-tag. Returns at most `want` actors.
 */
export async function pickCandidatesForCampaign({
  accountId,
  tags,
  denyTags,
  want,
  excludeDids,
}: PickArgs): Promise<DiscoveredActor[]> {
  // Normalised follow tags. A term can't be both something we search
  // FOR and something we deny: searching #nsfw means every result's
  // text contains "nsfw", so if "nsfw" were also a deny term we'd
  // reject 100% of candidates. Strip any deny term that collides with
  // a follow tag (the form ships NSFW/crypto as default deny tags, so
  // an #nsfw campaign hits this immediately).
  const followLc = new Set(
    tags
      .map((t) => t.trim().toLowerCase().replace(/^#+/, ''))
      .filter(Boolean),
  )
  const denyLc = denyTags
    .map((t) => t.toLowerCase().replace(/^#+/, ''))
    .filter(Boolean)
    .filter((d) => !followLc.has(d))
  const seen = new Set<string>(excludeDids)
  // Authors a deny-tag tainted in an earlier post — never add them even
  // if a later, clean post of theirs shows up.
  const denied = new Set<string>()
  const out: DiscoveredActor[] = []
  // Capture the last search failure so we can surface it instead of
  // silently reporting "0 candidates" when searchPosts actually errored.
  let lastErr: unknown
  for (const rawTag of tags) {
    if (out.length >= want) break
    const tag = rawTag.trim()
    if (!tag) continue
    let cursor: string | undefined
    for (let page = 0; page < MAX_PAGES_PER_TAG; page++) {
      if (out.length >= want) break
      let resp: PostSearchPage
      try {
        resp = await searchPostAuthorsByTag({ accountId, tag, limit: 100, cursor })
      } catch (e) {
        lastErr = e
        break
      }
      for (const hit of resp.hits) {
        if (out.length >= want) break
        const a = hit.author
        if (denied.has(a.did)) continue
        const haystack =
          `${a.handle} ${a.displayName ?? ''} ${hit.text}`.toLowerCase()
        if (denyLc.some((d) => haystack.includes(d))) {
          denied.add(a.did)
          continue
        }
        if (seen.has(a.did)) continue
        seen.add(a.did)
        out.push(a)
      }
      if (!resp.cursor) break
      cursor = resp.cursor
    }
  }
  // Found nothing AND a search call errored → surface the real reason.
  // autoFollow turns a thrown error into "search error: …" in the tick
  // message, so the diagnostics panel shows the actual 4xx/5xx instead
  // of the misleading "0 candidates".
  if (out.length === 0 && lastErr) {
    throw lastErr instanceof Error ? lastErr : new Error(String(lastErr))
  }
  return out
}
